import type { OpeningBalanceRow, OpeningBalancesPut } from '@morales/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../api';
import { useAuth } from '../auth';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { ErrorBox, PracticeBanner, QueryView } from '../components/ui';
import { formatDate, formatMoney, isIsoDate } from '../format';
import { useLeaveGuard } from '../hooks';
import { useLocale } from '../i18n';

/** What is typed for one customer: the opening balance, the oldest due date and a note; '' leaves a field as it is. */
type Typed = { amount?: string; dueDate?: string; note?: string };
type Line = OpeningBalanceRow & { typed: Typed | undefined };
type SortKey = 'customer' | 'balance' | 'opening' | 'due';

/** "1,250.50", "-80", "1250.5" -> a number to the cent; junk -> null. */
export function parseAmount(raw: string): number | null {
  const s = raw.trim().replace(/[$,\s]/g, '');
  if (!/^-?\d+(\.\d{1,2})?$/.test(s)) return null;
  return Math.round(Number(s) * 100) / 100;
}

/**
 * The lines to send: one per customer whose typed amount, due date or note differs from what is stored, with the
 * amount as the total wanted (the server writes the difference). Null when something typed cannot be saved.
 */
export function openingLines(rows: OpeningBalanceRow[], typed: Record<string, Typed>, start: string | null): OpeningBalancesPut['lines'] | null {
  const out: OpeningBalancesPut['lines'] = [];
  for (const r of rows) {
    const t = typed[r.customerId];
    if (!t) continue;
    const amount = t.amount === undefined ? r.amount : parseAmount(t.amount);
    if (amount === null) return null;
    const due = t.dueDate === undefined ? r.dueDate : t.dueDate.trim() === '' ? null : t.dueDate.trim();
    if (due !== null && (!isIsoDate(due) || (start !== null && due > start))) return null;
    const note = t.note === undefined ? undefined : t.note.trim();
    if (amount === r.amount && due === r.dueDate && (note === undefined || note === (r.note ?? ''))) continue;
    out.push({ customerId: r.customerId, amount, ...(due !== r.dueDate ? { dueDate: due } : {}), ...(note ? { note } : {}) });
  }
  return out;
}

/** Administration → the ledger start and every customer's opening balance (decision 74). Owner edits, office reads. */
export function LedgerPage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const owner = me?.role === 'owner';
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['ledger', 'opening'], queryFn: ({ signal }) => api.openingBalances(signal), placeholderData: keepPreviousData });
  const [typed, setTyped] = useState<Record<string, Typed>>({});
  const [q, setQ] = useState('');
  const [start, setStart] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const data = query.data;
  const lines = data ? openingLines(data.rows, typed, data.balanceFrom) : [];
  const dirty = Object.keys(typed).length > 0;
  useLeaveGuard(owner && dirty, t('openingUnsaved'));

  const save = useMutation({
    mutationFn: (v: { body: OpeningBalancesPut; key: string }) => api.saveOpeningBalances(v.body, v.key),
    retry: (count, error) => error instanceof ApiError && error.status === 0 && count < 3,
    onSuccess: (fresh, v) => {
      attempt.current = null;
      qc.setQueryData(['ledger', 'opening'], fresh);
      // only what was sent is cleared
      const sent = new Set(v.body.lines.map((l) => l.customerId));
      setTyped((cur) => Object.fromEntries(Object.entries(cur).filter(([id]) => !sent.has(id))));
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['reports'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
  const moveStart = useMutation({
    mutationFn: (date: string) => api.setLedgerStart(date),
    onSuccess: () => {
      setStart(null);
      void qc.invalidateQueries();
    },
  });
  const submit = () => {
    if (!lines || lines.length === 0) return;
    const body: OpeningBalancesPut = { lines };
    const serialized = JSON.stringify(body);
    if (attempt.current?.body !== serialized) attempt.current = { body: serialized, key: newIdempotencyKey() };
    setSaved(false);
    save.mutate({ body, key: attempt.current.key });
  };

  const sorters = useMemo(
    () => ({
      customer: byText<Line>((l) => l.customerName),
      balance: byNumber<Line>((l) => l.balance),
      opening: byNumber<Line>((l) => l.amount),
      due: byText<Line>((l) => l.dueDate),
    }),
    [],
  );
  const { sorted, head } = useSort<Line, SortKey>(sorters, { key: 'customer', dir: 1 }, ['balance', 'opening']);
  const words = q.trim().toLowerCase();
  const rows: Line[] = sorted((data?.rows ?? []).filter((r) => (r.active || r.amount !== 0) && (!words || r.customerName.toLowerCase().includes(words))).map((r) => ({ ...r, typed: typed[r.customerId] })));
  const money = (v: number) => formatMoney(v, intlLocale);
  const shownAmount = (l: Line) => (l.typed?.amount !== undefined ? parseAmount(l.typed.amount) : l.amount);
  // what is typed back to what is stored is no change: the cell stops being orange
  const edit = (row: OpeningBalanceRow, change: Typed) => {
    setSaved(false);
    const next: Typed = { ...typed[row.customerId], ...change };
    if (next.amount !== undefined && parseAmount(next.amount) === row.amount) delete next.amount;
    if (next.dueDate !== undefined && next.dueDate.trim() === (row.dueDate ?? '')) delete next.dueDate;
    if (next.note !== undefined && next.note.trim() === (row.note ?? '')) delete next.note;
    setTyped((cur) => {
      const rest = { ...cur };
      delete rest[row.customerId];
      return Object.keys(next).length ? { ...rest, [row.customerId]: next } : rest;
    });
  };
  const badDate = (l: Line) => {
    const d = l.typed?.dueDate;
    if (d === undefined || d.trim() === '') return false;
    return !isIsoDate(d.trim()) || (data?.balanceFrom !== null && data?.balanceFrom !== undefined && d.trim() > data.balanceFrom);
  };

  const columns: SheetColumn<Line>[] = [
    { key: 'customer', header: head('customer', t('customer')), label: t('customer'), width: '240px', render: (l) => <Link to={`/customers/${l.customerId}/statement`}>{l.customerName}</Link> },
    { key: 'balance', header: head('balance', t('balanceTodayCol')), label: t('balanceTodayCol'), width: '120px', align: 'right', render: (l) => money(l.balance) },
    {
      key: 'opening',
      header: head('opening', t('openingCol')),
      label: t('openingCol'),
      width: '130px',
      align: 'right',
      render: (l) => {
        const v = shownAmount(l);
        return v === null ? <span title={t('invalidPrices')}>{l.typed?.amount}</span> : v === 0 ? '' : <strong>{money(v)}</strong>;
      },
      edit: owner && data?.balanceFrom ? { kind: 'number', get: (l) => (l.typed?.amount ?? (l.amount ? String(l.amount) : '')), set: (l, v) => ({ ...l, typed: { ...l.typed, amount: v } }) } : undefined,
      canEdit: () => owner && !!data?.balanceFrom && !save.isPending,
      cellClass: (l) => (l.typed?.amount !== undefined ? (shownAmount(l) === null ? 'unsaved bad' : 'unsaved') : undefined),
    },
    {
      key: 'due',
      header: head('due', t('dueDateCol')),
      label: t('dueDateCol'),
      width: '130px',
      render: (l) => {
        const d = l.typed?.dueDate ?? l.dueDate ?? '';
        return badDate(l) ? <span title={t('notADate')}>{d}</span> : d ? formatDate(d, intlLocale, 'date') : '';
      },
      edit: owner && data?.balanceFrom ? { kind: 'text', get: (l) => l.typed?.dueDate ?? l.dueDate ?? '', set: (l, v) => ({ ...l, typed: { ...l.typed, dueDate: v } }) } : undefined,
      canEdit: () => owner && !!data?.balanceFrom && !save.isPending,
      cellClass: (l) => (l.typed?.dueDate !== undefined ? (badDate(l) ? 'unsaved bad' : 'unsaved') : undefined),
    },
    {
      key: 'note',
      header: t('noteCol'),
      width: '260px',
      render: (l) => l.typed?.note ?? l.note ?? '',
      edit: owner && data?.balanceFrom ? { kind: 'text', get: (l) => l.typed?.note ?? l.note ?? '', set: (l, v) => ({ ...l, typed: { ...l.typed, note: v } }) } : undefined,
      canEdit: () => owner && !!data?.balanceFrom && !save.isPending,
      cellClass: (l) => (l.typed?.note !== undefined ? 'unsaved' : undefined),
    },
    { key: 'entries', header: t('entriesCol'), width: '80px', align: 'right', render: (l) => (l.entries ? String(l.entries) : '') },
  ];
  const withOpening = (data?.rows ?? []).filter((r) => r.amount !== 0).length;

  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{t('ledgerTitle')}</h1>
          <p className="muted">{t('ledgerHint')}</p>
        </div>
      </header>
      {/* opening balances typed here are practice until the switch, like every money screen (decision 184) */}
      <PracticeBanner />
      <div className="toolbar">
        <label className="tool">
          <span>{t('ledgerStart')}</span>
          <input type="date" value={start ?? data?.balanceFrom ?? ''} readOnly={!owner} onChange={(e) => setStart(e.target.value)} aria-label={t('ledgerStart')} />
        </label>
        {owner && start && isIsoDate(start) && start !== data?.balanceFrom ? (
          <button type="button" className="btn btn-secondary btn-sm" disabled={moveStart.isPending} onClick={() => window.confirm(t('moveStartConfirm').replace('{date}', start)) && moveStart.mutate(start)}>
            {t('setStart')}
          </button>
        ) : null}
        <input className="tool-search" type="search" placeholder={t('search')} aria-label={t('search')} value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="spacer" />
        {owner ? (
          <button type="button" className="btn btn-primary btn-sm" disabled={save.isPending || lines === null || lines.length === 0} onClick={submit}>
            {t('saveOpeningN').replace('{n}', String(lines?.length ?? 0))}
          </button>
        ) : null}
      </div>
      {moveStart.isError ? <ErrorBox error={moveStart.error} /> : null}
      {save.isError ? <ErrorBox error={save.error} /> : null}
      {saved ? (
        <p className="note" role="status">
          {t('openingSaved')}
        </p>
      ) : null}
      {data && !data.balanceFrom ? <p className="warn-band">{t('ledgerNotStarted')}</p> : null}
      <dl className="totals-strip" data-testid="opening-strip">
        <div>
          <dt>{t('ledgerStart')}</dt>
          <dd>{data?.balanceFrom ? formatDate(data.balanceFrom, intlLocale, 'date') : '—'}</dd>
        </div>
        <div>
          <dt>{t('withOpening')}</dt>
          <dd>{withOpening}</dd>
        </div>
        <div>
          <dt>{t('openingTotal')}</dt>
          <dd>{money(data?.total ?? 0)}</dd>
        </div>
      </dl>
      <QueryView query={query}>
        {() => <Sheet label={t('ledgerTitle')} columns={columns} rows={rows} rowKey={(l) => l.customerId} rowLabel={(l) => l.customerName} stickyFirst minWidth="960px" onRowsChange={(next, at) => edit(next[at.row]!, next[at.row]!.typed ?? {})} />}
      </QueryView>
    </div>
  );
}
